import { useApiQuery } from "@gladis/api-client";
import type { SavingsChartDto, SavingsChartRange } from "@gladis/shared";
import { ErrorState, LoadingState, StackedAreaChart } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

const RANGES: { value: SavingsChartRange; label: string }[] = [
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "12m", label: "12 months" },
];

/**
 * How the household's savings have changed (FR-026 to FR-034).
 *
 * ONE LINE PER ACCOUNT, drawn from zero, so a line's height is that account's balance and two
 * accounts can be compared by eye. The household total lives in the header above, where it is a
 * number rather than something to be inferred from the top of a shape.
 *
 * A POINT PER RECORDED BALANCE. The chart bucketed by month until real balances went on screen, and
 * a household that recorded three in July saw one point for July — the last of the three. Every
 * entry is now a vertex.
 *
 * IT DERIVES NOTHING ELSE. The window and every value arrive from the server already resolved:
 * carry-forward, never-interpolate, the today cut-off, and archived accounts absent entirely. The
 * rules are stated once, on the side that has the clock and the data.
 *
 * The range is state HERE rather than in the URL. It is a way of looking at the page, not a
 * different page — nobody links a friend to "my savings, three-month view" — and putting it in the
 * URL would put a viewing preference into every link that gets shared or bookmarked.
 *
 * FR-034: with no non-archived accounts the series array is empty, `isEmpty` is true inside the
 * wrapper, and the Recharts chunk is never requested — the check sits before the lazy boundary.
 */
export function SavingsChart(): ReactNode {
  const t = useT();
  const [range, setRange] = useState<SavingsChartRange>("12m");

  const chart = useApiQuery<SavingsChartDto>(
    ["savings", "chart", range],
    `/savings/chart?range=${range}`,
  );

  const selector = (
    <div className="mb-3 flex gap-1" role="group" aria-label={t("savings.chart.range")}>
      {RANGES.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={range === option.value}
          onClick={() => setRange(option.value)}
          className={
            range === option.value
              ? "rounded-control border border-line-strong px-3 py-1 text-sm font-medium"
              : "rounded-control border border-transparent px-3 py-1 text-sm text-muted"
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  /*
   * The selector stays on screen through loading and failure.
   *
   * Switching range refetches, and hiding the control while that happens would move the thing the
   * member just clicked out from under the pointer — so a second click lands on whatever replaced
   * it. It is also the only way back if one range errors.
   */
  if (chart.isPending) {
    return (
      <>
        {selector}
        <LoadingState>{t("savings.chart.loading")}</LoadingState>
      </>
    );
  }

  if (chart.isError) {
    return (
      <>
        {selector}
        <ErrorState>{t("savings.chart.loadFailed")}</ErrorState>
      </>
    );
  }

  return (
    <>
      {selector}
      <StackedAreaChart
        series={chart.data.series}
        // LINES, not a stack. Each account is drawn from zero, so the height of its line is its own
        // balance. Stacked, an account's line tops out at the running total beneath it — correct,
        // and read by an actual member as that account holding the household's whole balance.
        variant="lines"
        emptyMessage={t("savings.chart.empty")}
      />
    </>
  );
}
